import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

const SERVICES = [
  {
    path: "/lead-generation-tools",
    title: "Lead Generation Tools",
    description: "Calculators, assessments and interactive tools that turn your expertise into qualified leads.",
  },
  {
    path: "/lead-generation-calculator",
    title: "Lead Generation Calculators",
    description: "Valuation, ROI and savings tools that give buyers a useful answer and capture qualified leads.",
  },
  {
    path: "/assessments",
    title: "Assessments",
    description: "Interactive scoring tools that qualify prospects before the first sales call, with every answer attached.",
  },
  {
    path: "/visualizers",
    title: "Visualizers",
    description: "Interactive product previews that help buyers see the outcome and move forward with confidence.",
  },
  {
    path: "/event-landing-pages",
    title: "Landing Pages",
    description: "High-impact pages and RSVP experiences for campaigns, launches and invite-only events.",
  },
  {
    path: "/custom-app-builds",
    title: "Full App Builds",
    description: "Custom apps with real accounts, data and logic, shipped in weeks, not quarters.",
  },
  {
    path: "/squarespace-migrations",
    title: "Squarespace Migrations",
    description: "Move from WordPress, Wix, Webflow or a custom CMS without losing traffic.",
  },
  {
    path: "/squarespace-websites",
    title: "Squarespace Sites",
    description: "New builds, redesigns and migrations your team can fully own and keep up to date.",
  },
  {
    path: "/linkedin-advertising",
    title: "LinkedIn Advertising",
    description: "Managed B2B campaigns that put your offer in front of the exact buyers it was built for.",
  },
  {
    path: "/seo-ai-visibility",
    title: "SEO & AI Visibility",
    description: "Technical signals and useful content that help buyers find you in search and AI-generated answers.",
  },
  {
    path: "/about",
    title: "About Square Migration",
    description: "The story behind the studio, from Squarespace migrations to lead-generation tools.",
  },
] as const;

interface RelatedServicesProps {
  /** Path of the current page, excluded from the list. */
  current: string;
}

export function RelatedServices({ current }: RelatedServicesProps) {
  const related = SERVICES.filter((s) => s.path !== current);
  return (
    <section className="px-6 md:px-8 lg:px-12 py-16 md:py-20 border-t border-border/50 bg-[#080E1E]/95 text-white">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-4">
          Explore More
        </p>
        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight mb-10 md:mb-14">
          What else we <span className="text-primary">build.</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {related.map((service) => (
            <Link
              key={service.path}
              to={service.path}
              className="group relative flex flex-col rounded-2xl bg-card/30 border border-border/50 p-6 transition-colors hover:border-primary/50"
            >
              <h3 className="text-xl font-semibold mb-2 flex items-center justify-between gap-3">
                {service.title}
                <ArrowRight
                  className="h-5 w-5 shrink-0 text-muted-foreground/50 transition-all group-hover:text-primary group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm">
                {service.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
